import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import ProductGridSection from "../components/ProductGridSection";
import RecoStrip from "../components/RecoStrip";
import PageSEO from "../components/SEO";
import CatalogHiddenBanner from "../components/CatalogHiddenBanner";
import useContent from "../lib/content";
import { useAuth } from "../lib/auth-context";
import { useShop, useShopHref } from "../lib/shop-context";

/**
 * Phase 39 — Catalog landing page.
 *
 * Extracted from `Home.jsx` when Home was cut down to just the 2 hero
 * tiles. This is what the "Enviar una compra" tile navigates to
 * (`/envio/tienda`). Renders search bar + "Para ti" (auth users only)
 * + "Destacados" + "Novedades" — exactly the shopping experience the
 * old home surfaced.
 *
 * The header search bar remains functional site-wide, so a user who
 * lands here via the tile can just start typing in the header without
 * needing this page's inline search. Both work.
 */
export default function Tienda() {
  const [novedades, setNovedades] = useState([]);
  const [novedadesLoading, setNovedadesLoading] = useState(true);
  const [featured, setFeatured] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  // Phase 39 fix — "Explorar catálogo" fallback fed by the raw /products
  // endpoint (no admin flag required). Guarantees the tienda page never
  // renders 100 % empty even if nobody has marked Destacados/Novedades yet.
  const [explorar, setExplorar] = useState([]);
  const [explorarLoading, setExplorarLoading] = useState(true);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const { user } = useAuth();
  const shopCtx = useShop();
  const shopHref = useShopHref();
  const shop = shopCtx?.shop || "envio";
  // Fase 4 · CMS — el banner (título / subtítulo / imagen) proviene del CMS
  // según el shop actual. Los defaults hardcoded siguen apareciendo si el
  // endpoint aún no responde.
  const { fields: content } = useContent(shop === "local" ? "local" : "envio");

  useEffect(() => {
    api
      .get("/products/new", { params: { limit: 24, shop } })
      .then((r) => setNovedades(r.data))
      .catch(() => {})
      .finally(() => setNovedadesLoading(false));
    api
      .get("/products/featured", { params: { limit: 24, shop } })
      .then((r) => setFeatured(r.data))
      .catch(() => {})
      .finally(() => setFeaturedLoading(false));
    api
      .get("/products", { params: { limit: 24, shop } })
      .then((r) => setExplorar(r.data))
      .catch(() => {})
      .finally(() => setExplorarLoading(false));
  }, [shop]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`${shopHref("/buscar")}?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div data-testid="tienda-page" className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-10">
      <PageSEO
        title={content.seo_title || "Tienda MercaGo · Catálogo Mercadona"}
        description={content.seo_description}
        canonical="https://mercago.one/envio/tienda"
      />

      {/* Fase 3 · Catalog kill-switch. */}
      {shopCtx?.catalog_hidden ? (
        <CatalogHiddenBanner />
      ) : (
        <>
      <header className="mb-4 sm:mb-6">
        <h1
          className="text-2xl sm:text-3xl font-black tracking-tight"
          data-testid={shop === "local" ? "local-banner-title" : "envio-banner-title"}
        >
          {content.banner_title || "Tienda MercaGo"}
        </h1>
        <p
          className="text-sm text-neutral-500 mt-1 whitespace-pre-line"
          data-testid={shop === "local" ? "local-banner-subtitle" : "envio-banner-subtitle"}
        >
          {content.banner_subtitle ||
            "Elige del catálogo Mercadona lo que quieres enviar a tu familia."}
        </p>
        {content.banner_image_url && (
          <img
            src={
              content.banner_image_url.startsWith("http")
                ? content.banner_image_url
                : `${window.location.origin}${content.banner_image_url}`
            }
            alt=""
            data-testid={shop === "local" ? "local-banner-image" : "envio-banner-image"}
            className="mt-4 w-full max-h-56 object-cover rounded-2xl"
            loading="eager"
          />
        )}
      </header>

      {/* Inline search — mirrors the header's search but keeps the shopping
          flow discoverable for users who land here from a tile click. */}
      <form
        onSubmit={submitSearch}
        data-testid="tienda-search"
        className="flex max-w-xl mx-auto"
      >
        <div className="relative w-full">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-500" />
          <Input
            data-testid="tienda-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="¿Qué necesitas hoy?"
            className="pl-12 pr-32 h-14 rounded-full bg-white placeholder:text-neutral-400 border border-neutral-200 focus-visible:ring-[#00953B] text-base"
          />
          <Button
            type="submit"
            data-testid="tienda-search-btn"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 h-11 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white px-6"
          >
            Buscar
          </Button>
        </div>
      </form>

      {user && (
        <RecoStrip
          context="home"
          testid="reco-strip-home"
          title="Para ti"
          variant="grid"
          seeAllHref={shopHref("/para-ti")}
          seeAllTestId="reco-strip-home-ver-todos"
        />
      )}

      <ProductGridSection
        testid="home-destacados"
        title="Destacados"
        subtitle="Productos seleccionados por MercaGo"
        items={featured}
        loading={featuredLoading}
        maxItems={8}
        seeAllHref={shopHref("/destacados")}
        seeAllTestId="home-destacados-ver-todos"
        emptyMessage="Aún no hay productos destacados. Márcalos como destacados desde el panel admin."
      />

      <ProductGridSection
        testid="home-novedades"
        title="Novedades"
        subtitle="Los últimos productos que llegan a MercaGo"
        items={novedades}
        loading={novedadesLoading}
        maxItems={8}
        seeAllHref={shopHref("/nuevo")}
        seeAllTestId="home-novedades-ver-todos"
        emptyMessage="Sin novedades por ahora."
      />

      {/* Phase 39 fix — general-catalog fallback. Never depends on admin
          flags, so the tienda page always shows something to browse. */}
      <ProductGridSection
        testid="tienda-explorar"
        title="Explorar catálogo"
        subtitle="Todos los productos disponibles"
        items={explorar}
        loading={explorarLoading}
        maxItems={24}
        seeAllHref={shopHref("/buscar")}
        seeAllTestId="tienda-explorar-ver-todos"
        emptyMessage="El catálogo está vacío en este momento."
      />
        </>
      )}
    </div>
  );
}
